import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { AppShellV8 } from "../AppShellV8";
import { ROSTER } from "../roster";
import { agentsFetch } from "../api";
import { getActiveClientSlug, setActiveClient } from "../activeClient";
import { getSessionToken } from "@/api/auth";
import { PasswordPanel } from "@/Settings/Password/PasswordPanel";
import { AppContext } from "@/AppContext";
import { getBasicUserInfo } from "@/api/auth";
import { useUnifiedShell } from "@/access/useUnifiedShell";
import { settingsTabsFor } from "@/access/userTypes";
import { NEW_TAB_META, NEW_TAB_KEYS } from "@/Settings/settingsTabs";
import { DomainsDashboard } from "@/Settings/DomainManagement/DomainsDashboard";
import { Integrations } from "@/Settings/IntegrationManagement/Integrations";
import { ConnectClaude } from "@/Apps/Sites/ConnectClaude";
import { getTeamInfo, setTeamMember, type TeamInfo } from "@/Apps/Reviews/api";
import { TeamRoleControls, roleLabel, canRemove, type TeamRoles } from "./TeamRoleControls";
import { listClientLinks, linkClientAccount, unlinkClientAccount, linkForClient, linkedLabel, type ClientLink } from "../clientLinks";

// AMB-435 — slug a client name the same way the operator path layer does, so a Settings
// archive/create call addresses the right tenant folder.
const toClientSlug = (s: string) =>
  s.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

type SettingsTab = "profile" | "team" | "clients" | "integrations" | "notifications" | "billing" | "domains" | "connections" | "claude";

const TAB_META: { key: SettingsTab; label: string; icon: JSX.Element }[] = [
  {
    key: "profile",
    label: "Profile",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
  },
  {
    key: "team",
    label: "Team",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
  {
    key: "clients",
    label: "Clients",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
      </svg>
    ),
  },
  {
    key: "integrations",
    label: "Integrations",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.556a4.5 4.5 0 00-1.242-7.244l4.5-4.5a4.5 4.5 0 016.364 6.364l-1.757 1.757" />
      </svg>
    ),
  },
  {
    key: "notifications",
    label: "Notifications",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
      </svg>
    ),
  },
  {
    key: "billing",
    label: "Billing",
    icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
      </svg>
    ),
  },
  ...NEW_TAB_META, // Domains / Integrations / Connect Claude — shown only with the unified shell (see visibleTabs)
].filter((t) => !["integrations", "notifications", "billing"].includes(t.key)) as { key: SettingsTab; label: string; icon: JSX.Element }[]; // trial: hide unwired settings tabs

/* ─── Integrations data ─── */
interface IntegrationRow {
  name: string;
  icon: string;
  color: string;
  status: "connected" | "syncing" | "disconnected";
  lastSync: string;
  events24h: number;
}

const INTEGRATIONS: IntegrationRow[] = [
  { name: "Zoom", icon: "Z", color: "bg-blue-600", status: "syncing", lastSync: "Syncing now...", events24h: 14 },
  { name: "Google Calendar", icon: "G", color: "bg-blue-500", status: "connected", lastSync: "5 min ago", events24h: 38 },
  { name: "Slack", icon: "S", color: "bg-purple-900", status: "connected", lastSync: "1 min ago", events24h: 247 },
  { name: "Google Drive", icon: "D", color: "bg-yellow-500", status: "connected", lastSync: "12 min ago", events24h: 19 },
  { name: "Otter.ai", icon: "O", color: "bg-blue-500", status: "connected", lastSync: "3 min ago", events24h: 8 },
  { name: "HubSpot", icon: "H", color: "bg-orange-600", status: "connected", lastSync: "8 min ago", events24h: 52 },
  { name: "Asana", icon: "A", color: "bg-orange-500", status: "connected", lastSync: "15 min ago", events24h: 31 },
  { name: "Gmail", icon: "M", color: "bg-red-500", status: "connected", lastSync: "2 min ago", events24h: 126 },
];

/* ─── Team data ─── */
interface TeamMember {
  id?: string; // platform user id for real tenants; absent for the hardcoded Ambid dogfood roster
  name: string;
  email: string;
  initials: string;
  color: string;
  role: "Admin" | "Manager" | "Team Member";
  permissions: {
    approveReviews: boolean;
    editKB: boolean;
    manageClients: boolean;
    viewAgentActivity: boolean;
    manageIntegrations: boolean;
  };
}

// Team roster is DERIVED from the canonical roster.ts (AMB-359). Who is a Team manager and who
// can share comes from the server's team list (SH-7), never from names written here.
const TEAM_MEMBERS: TeamMember[] = ROSTER.map((m) => ({
  name: m.name, email: m.email, initials: m.initials, color: m.color, role: "Team Member",
  permissions: { approveReviews: false, editKB: true, manageClients: false, viewAgentActivity: true, manageIntegrations: false },
}));

/* ─── Client Access data ─── */
interface ClientAccess {
  slug?: string; // stable operator-API identity (folder slug); display fields below can change
  name: string;
  initials: string;
  color: string;
  industry: string;
  primaryColor: string; // brand color (hex); "" means none set → fall back to the palette class
  campaigns?: number;
  deliverables?: number;
}

/* ─── Notification rules data (Priority removed for v7) ─── */
interface NotificationRule {
  trigger: string;
  channels: { inApp: boolean; email: boolean; slack: boolean; push: boolean };
}

const NOTIFICATION_RULES: NotificationRule[] = [
  { trigger: "New Review Assigned", channels: { inApp: true, email: true, slack: true, push: true } },
  { trigger: "Review Completed", channels: { inApp: true, email: false, slack: true, push: false } },
  { trigger: "Meeting Processed", channels: { inApp: true, email: false, slack: true, push: false } },
  { trigger: "Deliverable Generated", channels: { inApp: true, email: false, slack: false, push: false } },
  { trigger: "Deadline Approaching", channels: { inApp: true, email: true, slack: true, push: true } },
  { trigger: "Agent Error", channels: { inApp: true, email: true, slack: true, push: true } },
];

/* ─── Role badge styles (SH-7: Team manager / Team member) ─── */
const roleStyle = (r?: TeamRoles) => (r?.manager ? "bg-blue-50 text-blue-700" : "bg-gray-100 text-gray-600");

const CHANNEL_LABELS: Record<string, string> = {
  inApp: "In-App",
  email: "Email",
  slack: "Slack",
  push: "Push",
};

/* ─── Shared avatar/role helpers (module-level so the member loader + create handlers share them) ─── */
const PALETTE = ["bg-blue-500", "bg-emerald-500", "bg-purple-500", "bg-pink-500", "bg-amber-500", "bg-cyan-500"];
const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "CL";
const defaultPermsFor = (role: string): TeamMember["permissions"] => ({
  approveReviews: role === "Admin" || role === "Manager",
  editKB: true,
  manageClients: role === "Admin" || role === "Manager",
  viewAgentActivity: true,
  manageIntegrations: role === "Admin",
});

/* ─── Channel icon helpers ─── */
function ChannelIcon({ channel, active }: { channel: string; active: boolean }) {
  const base = `h-4 w-4 transition-all duration-150 ${active ? "text-gray-700" : "text-gray-300"}`;
  switch (channel) {
    case "inApp":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
      );
    case "email":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
        </svg>
      );
    case "slack":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
        </svg>
      );
    case "push":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
        </svg>
      );
    default:
      return null;
  }
}

/* ─── Per-notification-type icon paths ─── */
function NotificationTypeIcon({ trigger }: { trigger: string }) {
  const base = "h-4.5 w-4.5 text-gray-500";
  switch (trigger) {
    case "New Review Assigned":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15a2.25 2.25 0 012.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
        </svg>
      );
    case "Review Completed":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "Meeting Processed":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
        </svg>
      );
    case "Deliverable Generated":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-.693a9 9 0 016.208.682l.108.054a9 9 0 006.086.71l3.114-.732a48.524 48.524 0 01-.005-10.499l-3.11.732a9 9 0 01-6.085-.711l-.108-.054a9 9 0 00-6.208-.682L3 4.5M3 15V4.5" />
        </svg>
      );
    case "Deadline Approaching":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case "Agent Error":
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
        </svg>
      );
    default:
      return (
        <svg className={base} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
        </svg>
      );
  }
}

/* ─── Shared ⋯ overflow menu (portal-positioned, click-outside backdrop) ─── */
interface OverflowMenuItem {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  icon?: JSX.Element;
}

function OverflowMenu({ items, label = "More actions" }: { items: OverflowMenuItem[]; label?: string }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  const openMenu = () => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      // Anchor menu's top-right to the button's bottom-right
      setCoords({ top: rect.bottom + 6, left: rect.right - 160 });
    }
    setOpen(true);
  };

  return (
    <>
      <button
        ref={btnRef}
        aria-label={label}
        onClick={(e) => {
          e.stopPropagation();
          open ? setOpen(false) : openMenu();
        }}
        className="rounded p-1.5 text-gray-400 opacity-0 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
        </svg>
      </button>
      {open &&
        coords &&
        createPortal(
          <>
            {/* click-outside backdrop */}
            <div className="fixed inset-0 z-[90]" onClick={() => setOpen(false)} />
            <div
              className="fixed z-[91] w-40 rounded-lg bg-white py-1 text-[13px] shadow-xl ring-1 ring-black/[0.06]"
              style={{ top: coords.top, left: Math.max(8, coords.left) }}
              onClick={(e) => e.stopPropagation()}
            >
              {items.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                  className={`flex w-full items-center gap-2.5 px-3 py-1.5 text-left transition-all duration-150 hover:bg-gray-50 ${
                    item.destructive ? "text-red-600" : "text-gray-700"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>
          </>,
          document.body
        )}
    </>
  );
}

const EDIT_ICON = (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
  </svg>
);
const TRASH_ICON = (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
  </svg>
);

/* ─── Shared modal shell (portal, click-outside, escape) ─── */
function Modal({
  title,
  onClose,
  onSave,
  saveLabel = "Save changes",
  children,
}: {
  title: string;
  onClose: () => void;
  onSave: () => void;
  saveLabel?: string;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gray-900/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/[0.06]">
        <div className="flex items-center justify-between border-b border-black/[0.06] px-5 py-4">
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="space-y-4 px-5 py-5">{children}</div>
        <div className="flex items-center justify-end gap-2 border-t border-black/[0.06] bg-gray-50/60 px-5 py-3.5">
          <button
            onClick={onClose}
            className="rounded-lg border border-black/[0.08] bg-white px-3.5 py-2 text-[13px] font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]"
          >
            {saveLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─── Reusable labeled text field for modals ─── */
function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-gray-600">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-gray-900 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      />
    </div>
  );
}

/* ─── Bottom undo toast ─── */
function UndoToast({ message, onUndo, onDismiss }: { message: string; onUndo: () => void; onDismiss: () => void }) {
  return createPortal(
    <div className="fixed bottom-6 left-1/2 z-[120] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-xl bg-gray-900 px-4 py-2.5 text-[13px] text-white shadow-xl ring-1 ring-black/[0.2]">
        <span>{message}</span>
        <span className="text-gray-600">·</span>
        <button
          onClick={onUndo}
          className="font-semibold text-white transition-all duration-150 hover:text-gray-200 active:scale-[0.97]"
        >
          Undo
        </button>
        <button
          onClick={onDismiss}
          className="rounded p-0.5 text-gray-400 transition-all duration-150 hover:text-white"
          aria-label="Dismiss"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>,
    document.body
  );
}

/* ════════════════════════════════════════════════════════════════════════════
   Main Component
   ════════════════════════════════════════════════════════════════════════════ */

const VALID_TABS: SettingsTab[] = ["profile", "team", "clients", "integrations", "notifications", "billing", ...NEW_TAB_KEYS];

export function Settings() {
  /* Navigation (used by Sign Out — mirrors AvatarDropdown's logout mechanism) */
  const navigate = useNavigate();
  const location = useLocation();

  /* One Settings page: with the unified shell on, the tab list comes from the access rules
     (settingsTabsFor); with it off, the page is exactly as before (no new tabs). */
  const unifiedShell = useUnifiedShell();
  const { permissions: userPermissions = [] } = useContext(AppContext);
  const allowedTabs: string[] = unifiedShell ? settingsTabsFor({ email: getBasicUserInfo()?.email, permissions: userPermissions }) : [];
  const visibleTabs = TAB_META.filter((t) => (NEW_TAB_KEYS as string[]).includes(t.key) ? allowedTabs.includes(t.key) : unifiedShell ? allowedTabs.includes(t.key) : true);

  /* Deep-link support (AMB-359/A3): the avatar user-menu opens Settings on a specific tab
     via ?tab=team etc. Read it on mount and whenever the query changes. */
  const tabFromUrl = (): SettingsTab => {
    const t = new URLSearchParams(location.search).get("tab") as SettingsTab | null;
    if (t && (NEW_TAB_KEYS as string[]).includes(t) && !allowedTabs.includes(t)) return "profile";
    return t && VALID_TABS.includes(t) ? t : "profile";
  };
  const [tab, setTab] = useState<SettingsTab>(tabFromUrl);
  useEffect(() => {
    setTab(tabFromUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  /* Sign Out — the agent logout path (the platform avatar dropdown is hidden for
     agents). Clear the local session (token + user, written at login) so the user
     is actually logged out, then go to /login. */
  const handleSignOut = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
    } catch { /* ignore */ }
    navigate("/login");
  };

  /* Integration state */
  const [integrations] = useState(INTEGRATIONS);

  /* Team state */
  const [teamMembers, setTeamMembers] = useState(TEAM_MEMBERS);
  /* SH-7 — Team manager role + Share permission, from the server's team list */
  const [teamInfo, setTeamInfo] = useState<TeamInfo | null>(null);
  const [teamLoadFailed, setTeamLoadFailed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getTeamInfo().then((d) => { if (!cancelled) setTeamInfo(d); }).catch(() => { if (!cancelled) setTeamLoadFailed(true); });
    return () => { cancelled = true; };
  }, []);
  const rolesOf = (email: string): TeamRoles => {
    const r = teamInfo?.team.find((m) => String(m.email).toLowerCase() === String(email || "").toLowerCase());
    return { email, manager: !!r?.manager, share: !!r?.share, owner: !!r?.owner };
  };
  const roleSaved = (m: { email: string; manager: boolean; share: boolean }) =>
    setTeamInfo((d) => d && { ...d, team: d.team.map((t) => (t.email.toLowerCase() === m.email.toLowerCase() ? { ...t, ...m } : t)) });

  /* Notification state */
  const [notifRules, setNotifRules] = useState(NOTIFICATION_RULES);

  /* Client state — loaded from the real per-tenant client roster (GET /api/agents/clients).
     For Ambid this is its real banks; for a brand-new tenant it's empty until they add one. */
  const [clients, setClients] = useState<ClientAccess[]>([]);
  useEffect(() => {
    let cancelled = false;
    agentsFetch("/api/agents/clients")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || !Array.isArray(d.items)) return;
        setClients(
          d.items.map((c: any, i: number) => ({
            slug: c.slug,
            name: c.name,
            initials: c.initials || initialsOf(c.name),
            color: c.color || PALETTE[i % PALETTE.length],
            industry: c.industry || "",
            primaryColor: c.primaryColor || "", // "" → use the palette class for the avatar
            campaigns: c.campaigns ?? 0,
            deliverables: c.deliverables ?? 0,
          }))
        );
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /* AMB-717 — Launch OS account links per client (core capability: Sites, Funnels, Claude all use
     them). Only the agency owner can link/unlink; the API enforces that and we show its message. */
  const [clientLinks, setClientLinks] = useState<ClientLink[]>([]);
  const [linkTarget, setLinkTarget] = useState<ClientAccess | null>(null);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkPassword, setLinkPassword] = useState("");
  const [linkError, setLinkError] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const loadClientLinks = useCallback(() => {
    const token = getSessionToken();
    if (!token) return;
    listClientLinks(token).then((l) => setClientLinks(Array.isArray(l) ? l : [])).catch(() => {});
  }, []);
  useEffect(() => { loadClientLinks(); }, [loadClientLinks]);
  const openLink = (c: ClientAccess) => { setLinkTarget(c); setLinkEmail(""); setLinkPassword(""); setLinkError(""); };
  const closeLink = () => { setLinkTarget(null); setLinkPassword(""); setLinkError(""); };
  const submitLink = async () => {
    if (!linkTarget || linkBusy) return;
    if (!linkEmail.trim() || !linkPassword) { setLinkError("Enter the client account's email and password."); return; }
    setLinkBusy(true);
    setLinkError("");
    try {
      await linkClientAccount(getSessionToken() || "", { email: linkEmail.trim(), password: linkPassword, clientName: linkTarget.name, clientSlug: linkTarget.slug });
      closeLink();
      loadClientLinks();
    } catch (e: any) {
      setLinkError(e?.message || "Couldn't link that account. Try again.");
    } finally {
      setLinkPassword("");
      setLinkBusy(false);
    }
  };
  const unlinkClient = async (link: ClientLink, name: string) => {
    if (!window.confirm(`Unlink ${name}'s Launch OS account? Your team loses access to it right away. You can link it again later.`)) return;
    try {
      await unlinkClientAccount(getSessionToken() || "", link.id);
      loadClientLinks();
    } catch (e: any) {
      window.alert(e?.message || "Couldn't unlink. Try again.");
    }
  };

  /* Soft-delete state (archived clients / removed members) */
  const [archivedClients, setArchivedClients] = useState<Set<string>>(new Set());
  const [removedMembers, setRemovedMembers] = useState<Set<string>>(new Set());

  /* Undo toast state */
  const [toast, setToast] = useState<{ message: string; onUndo: () => void } | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = useCallback((message: string, onUndo: () => void) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, onUndo });
    toastTimer.current = setTimeout(() => setToast(null), 6000);
  }, []);
  const dismissToast = useCallback(() => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast(null);
  }, []);

  /* Edit-modal state */
  const [editClient, setEditClient] = useState<{ idx: number; draft: ClientAccess } | null>(null);
  const [editMember, setEditMember] = useState<{ idx: number; draft: TeamMember } | null>(null);

  /* Create-modal state */
  const [showAddClient, setShowAddClient] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [newClient, setNewClient] = useState({ name: "", industry: "", primaryColor: "#3B82F6" });
  const [newMember, setNewMember] = useState({ name: "", email: "", role: "Team Member" as TeamMember["role"] });

  /* Unsaved-changes (dirty) state — drives the sticky footer, which saves the Profile tab.
     Role/permission edits persist instantly on change (below), so they don't use this flag. */
  const [dirty, setDirty] = useState(false);
  const markDirty = () => setDirty(true);

  // Round 4 — owner Profile read/save. Defaults show the Ambid dogfood values; a real tenant's
  // GET /api/agents/profile overwrites them. POST persists (owner-only; the dogfood 401s, so the
  // local edit simply stands). Scope (Kyle, 2026-06-20): owner name + agency name only.
  const [profile, setProfile] = useState({ ownerName: "Kyle Graham", agencyName: "Ambid Technologies", email: "kjgraham@gmail.com" });
  const [savingProfile, setSavingProfile] = useState(false);
  useEffect(() => {
    let cancelled = false;
    agentsFetch("/api/agents/profile")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || (!d.ownerName && !d.agencyName)) return; // empty → keep dogfood display
        setProfile({ ownerName: d.ownerName || "", agencyName: d.agencyName || "", email: d.email || "" });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await agentsFetch("/api/agents/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ownerName: profile.ownerName, agencyName: profile.agencyName }),
      });
    } catch { /* dogfood 401 / offline — the local edit stands */ }
    setSavingProfile(false);
    setDirty(false);
  };
  const handleSave = () => { void saveProfile(); };

  // Round 4 — load the real per-tenant roster (AMB-431). For a real tenant this REPLACES the
  // hardcoded TEAM_MEMBERS with the agency's own members (owner + invited), carrying their
  // persisted role + permissions; for the Ambid dogfood GET /members is empty, so the frontend
  // ROSTER stays. The member `id` lets role/permission edits + remove address the real account.
  useEffect(() => {
    let cancelled = false;
    agentsFetch("/api/agents/members")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d || !Array.isArray(d.members) || d.members.length === 0) return;
        const mapped: TeamMember[] = d.members.map((m: any, i: number) => {
          const role: TeamMember["role"] =
            m.role === "owner" ? "Admin" : ["Admin", "Manager", "Team Member"].includes(m.role) ? m.role : "Team Member";
          return {
            id: m.id,
            name: m.name || m.email,
            email: m.email,
            initials: m.initials || initialsOf(m.name || m.email),
            color: m.color || PALETTE[i % PALETTE.length],
            role,
            permissions: m.permissions || defaultPermsFor(role),
          };
        });
        setTeamMembers(mapped);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  /* Visible (non-soft-deleted) collections */
  const visibleClients = clients
    .map((c, idx) => ({ c, idx }))
    .filter(({ c }) => !archivedClients.has(c.initials));
  const visibleMembers = teamMembers
    .map((m, idx) => ({ m, idx }))
    .filter(({ m }) => !removedMembers.has(m.email));

  /* Per-tab count/context badges for the vertical nav */
  const connectedCount = integrations.filter((i) => i.status !== "disconnected").length;
  const TAB_COUNTS: Partial<Record<SettingsTab, number>> = {
    team: visibleMembers.length,
    clients: visibleClients.length,
    integrations: connectedCount,
  };

  /* ─── Soft-delete + undo handlers ─── */
  const archiveClient = (initials: string, name: string) => {
    setArchivedClients((prev) => new Set(prev).add(initials));
    // AMB-435 — soft-archive server-side (reversible; no-op/401 for the dogfood).
    agentsFetch("/api/agents/client/archive", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ client: toClientSlug(name) }),
    }).catch(() => {});
    showToast(`Removed ${name}`, () =>
      setArchivedClients((prev) => {
        const next = new Set(prev);
        next.delete(initials);
        return next;
      })
    );
  };
  const removeMember = (email: string, name: string, id?: string) => {
    setRemovedMembers((prev) => new Set(prev).add(email));
    // AMB-435 — revoke access server-side (clear memberOf + strip agents; reversible). Needs
    // the platform user id (carried on the loaded roster member); skip if unknown (e.g. dogfood).
    if (id) {
      agentsFetch("/api/agents/member/remove", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      }).catch(() => {});
    }
    showToast(`Removed ${name}`, () =>
      setRemovedMembers((prev) => {
        const next = new Set(prev);
        next.delete(email);
        return next;
      })
    );
  };

  /* ─── Create handlers ─── */
  const createClient = async () => {
    const name = newClient.name.trim();
    if (!name) return;
    const slug = toClientSlug(name);
    let initials = initialsOf(name);
    const existing = new Set(clients.map((c) => c.initials));
    while (existing.has(initials)) initials = initials + (clients.length % 9);
    const color = PALETTE[clients.length % PALETTE.length];
    const industry = newClient.industry.trim();
    // Only treat the color as a brand color if it differs from the picker's default, so an
    // untouched new client keeps the varied palette avatar rather than all going default-blue.
    const colorChanged = !!newClient.primaryColor && newClient.primaryColor.toLowerCase() !== "#3b82f6";
    const primaryColor = colorChanged ? newClient.primaryColor : "";
    const next: ClientAccess = { slug, name, initials, color, industry, primaryColor, campaigns: 0, deliverables: 0 };
    setClients((prev) => [...prev, next]);
    setShowAddClient(false);
    setNewClient({ name: "", industry: "", primaryColor: "#3B82F6" });
    // Create the folder, then persist any display metadata (industry / chosen brand color).
    try {
      await agentsFetch("/api/agents/client/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (industry || primaryColor) {
        await agentsFetch("/api/agents/client/meta", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ slug, name, industry, primaryColor }),
        });
      }
    } catch { /* dogfood 401 / offline — local state stands */ }
  };

  const createMember = () => {
    const name = newMember.name.trim();
    const email = newMember.email.trim();
    if (!name || !email) return;
    const color = PALETTE[teamMembers.length % PALETTE.length];
    const next: TeamMember = {
      name,
      email,
      initials: initialsOf(name),
      color,
      role: newMember.role,
      permissions: {
        approveReviews: newMember.role !== "Team Member",
        editKB: true,
        manageClients: newMember.role !== "Team Member",
        viewAgentActivity: true,
        manageIntegrations: newMember.role === "Admin",
      },
    };
    setTeamMembers((prev) => [...prev, next]);
    markDirty();
    setShowInviteMember(false);
    setNewMember({ name: "", email: "", role: "Team Member" });
    // AMB-435 — create/attach the platform user + roster (no-op/401 for the dogfood).
    agentsFetch("/api/agents/members", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, role: newMember.role }),
    }).catch(() => {});
  };

  /* ─── Edit save handlers ─── */
  const saveEditClient = () => {
    if (!editClient) return;
    const draft = editClient.draft;
    setClients((prev) => prev.map((c, i) => (i === editClient.idx ? draft : c)));
    setEditClient(null);
    if (!draft.slug) return; // local-only fallback (shouldn't happen for server-loaded clients)
    // Persist the display fields to the client's .client.json (folder/slug identity unchanged).
    agentsFetch("/api/agents/client/meta", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slug: draft.slug, name: draft.name, primaryColor: draft.primaryColor, industry: draft.industry }),
    }).catch(() => {});
    // If this is the client currently selected in the topbar, reflect the rename/recolor there too.
    if (getActiveClientSlug() === draft.slug) {
      setActiveClient({ slug: draft.slug, name: draft.name, initials: draft.initials, color: draft.color, primaryColor: draft.primaryColor });
    }
  };
  const saveEditMember = () => {
    if (!editMember) return;
    setTeamMembers((prev) => prev.map((m, i) => (i === editMember.idx ? editMember.draft : m)));
    markDirty();
    setEditMember(null);
  };

  const toggleNotifChannel = (ruleIdx: number, channel: string) => {
    markDirty();
    setNotifRules((prev) =>
      prev.map((r, i) =>
        i === ruleIdx
          ? { ...r, channels: { ...r.channels, [channel]: !(r.channels as Record<string, boolean>)[channel] } }
          : r
      )
    );
  };

  return (
    <AppShellV8 activeApp="settings" breadcrumb={["Settings"]} reviewCount={7}>
      <div className="flex h-full">
        {/* ── Vertical Sidebar Tabs ── */}
        <div className="w-52 shrink-0 border-r border-black/[0.06] bg-gray-50/50 p-3">
          <nav className="space-y-0.5">
            {visibleTabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-150 ${
                  tab === t.key
                    ? "bg-gray-900 text-white shadow-sm"
                    : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                }`}
              >
                <span className={`transition-all duration-150 ${tab === t.key ? "text-white" : "text-gray-400"}`}>
                  {t.icon}
                </span>
                <span className="flex-1 text-left">{t.label}</span>
                {TAB_COUNTS[t.key] !== undefined && (
                  <span
                    className={`rounded-full px-1.5 text-[10px] font-medium leading-[18px] transition-all duration-150 ${
                      tab === t.key ? "bg-white/15 text-white/80" : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {TAB_COUNTS[t.key]}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* ── Main Content Area ── */}
        <div className="relative flex-1 overflow-auto">
          <div className="mx-auto max-w-3xl px-8 py-8 pb-24">

            {/* ════════════════════════════════════════════════════════════════
                TAB — PROFILE
                ════════════════════════════════════════════════════════════════ */}
            {tab === "domains" && <DomainsDashboard embedded />}
            {tab === "connections" && <Integrations embedded />}
            {tab === "claude" && <ConnectClaude embedded />}

            {tab === "profile" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
                  <p className="mt-0.5 text-xs text-gray-500">Manage your personal information and preferences</p>
                </div>

                {/* Avatar + name card */}
                <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-6">
                  <div className="flex items-center gap-5">
                    <div className="relative">
                      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-500 text-lg font-bold text-white">
                        {initialsOf(profile.ownerName)}
                      </div>
                      <button className="absolute -bottom-0.5 -right-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-black/[0.08] transition-all duration-150 hover:bg-gray-50">
                        <svg className="h-3 w-3 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                        </svg>
                      </button>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">{profile.ownerName || "—"}</h3>
                      <p className="text-xs text-gray-500">{profile.agencyName || "—"}</p>
                      <span className="mt-1 inline-flex items-center rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-white">
                        Owner
                      </span>
                    </div>
                  </div>
                </div>

                {/* Account details */}
                <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-5">Account</h3>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Your Name</label>
                      <input
                        type="text"
                        value={profile.ownerName}
                        onChange={(e) => { setProfile((p) => ({ ...p, ownerName: e.target.value })); markDirty(); }}
                        className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-gray-900 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                    <div>
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Email</label>
                      <input
                        type="email"
                        value={profile.email}
                        readOnly
                        className="w-full cursor-not-allowed rounded-lg border border-black/[0.08] bg-gray-50 px-3 py-2 text-[13px] text-gray-500"
                      />
                      <p className="mt-1 text-[11px] text-gray-400">This is your sign-in email and can't be changed here.</p>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-xs font-medium text-gray-600">Agency Name</label>
                      <input
                        type="text"
                        value={profile.agencyName}
                        onChange={(e) => { setProfile((p) => ({ ...p, agencyName: e.target.value })); markDirty(); }}
                        className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-gray-900 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                      />
                    </div>
                  </div>
                </div>

                <PasswordPanel />

                {/* Sign out */}
                <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Sign Out</h3>
                      <p className="mt-0.5 text-xs text-gray-500">End your session on this device</p>
                    </div>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-[13px] font-medium text-red-600 transition-all duration-150 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500/20"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </div>

              </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                TAB — TEAM
                ════════════════════════════════════════════════════════════════ */}
            {tab === "team" && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Team Members</h2>
                    <p className="mt-0.5 text-xs text-gray-500">Team managers can share, and choose who else on the team can share</p>
                  </div>
                  <button
                    onClick={() => setShowInviteMember(true)}
                    className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-[13px] font-medium text-white transition-all duration-150 hover:bg-gray-800 active:scale-[0.98]"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Invite Member
                  </button>
                </div>

                {teamLoadFailed && (
                  <p role="alert" className="mb-3 rounded-lg bg-amber-50 px-4 py-2.5 text-[13px] text-amber-800">
                    Couldn't load team roles. Refresh the page to try again.
                  </p>
                )}

                {/* Team member cards */}
                <div className="space-y-3">
                  {visibleMembers.map(({ m: member, idx: memberIdx }) => (
                    <div key={member.email} className="group rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] transition-all duration-150">
                      <div className="flex items-center justify-between p-5">
                        {/* Left: avatar + info */}
                        <div className="flex items-center gap-3.5">
                          <div className={`flex h-10 w-10 items-center justify-center rounded-full ${member.color} text-sm font-bold text-white`}>
                            {member.initials}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="text-[13px] font-semibold text-gray-900">{member.name}</h3>
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${roleStyle(rolesOf(member.email))}`}>
                                {roleLabel(rolesOf(member.email))}
                              </span>
                            </div>
                            <p className="mt-0.5 text-xs text-gray-400">{member.email}</p>
                          </div>
                        </div>

                        {/* Right: Team manager role + Share, then edit / remove */}
                        <div className="flex items-center gap-3">
                          <TeamRoleControls
                            name={member.name}
                            roles={rolesOf(member.email)}
                            canManage={!!teamInfo?.me.manager}
                            save={setTeamMember}
                            onSaved={roleSaved}
                          />
                          <OverflowMenu
                            label={`Actions for ${member.name}`}
                            items={[
                              {
                                label: "Edit",
                                icon: EDIT_ICON,
                                onClick: () => setEditMember({ idx: memberIdx, draft: { ...member } }),
                              },
                              ...(canRemove(teamInfo, member.email)
                                ? [
                                    {
                                      label: "Remove",
                                      icon: TRASH_ICON,
                                      destructive: true,
                                      onClick: () => removeMember(member.email, member.name, member.id),
                                    },
                                  ]
                                : []),
                            ]}
                          />
                        </div>
                      </div>
                      {/* Per-member permission toggles are hidden until they're enforced (not just stored). */}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                TAB — INTEGRATIONS
                ════════════════════════════════════════════════════════════════ */}
            {tab === "integrations" && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-900">Connected Integrations</h2>
                    <p className="mt-0.5 text-xs text-gray-500">Manage your connected services and API access</p>
                  </div>
                  <div className="flex items-center gap-2 rounded-full border border-green-200 bg-green-50 px-3 py-1.5">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                    </span>
                    <span className="text-xs font-medium text-green-700">All Systems Operational</span>
                  </div>
                </div>

                {/* Integration cards grid */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {integrations.map((int) => (
                    <div
                      key={int.name}
                      className="group rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-4 transition-all duration-150 hover:shadow-md hover:ring-black/[0.08]"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${int.color} text-xs font-bold text-white shadow-sm`}>
                            {int.icon}
                          </span>
                          <div>
                            <h3 className="text-[13px] font-semibold text-gray-900">{int.name}</h3>
                            <div className="mt-0.5 flex items-center gap-1.5">
                              {int.status === "syncing" ? (
                                <>
                                  <span className="relative flex h-1.5 w-1.5">
                                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75" />
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-blue-500" />
                                  </span>
                                  <span className="text-[11px] font-medium text-blue-600">Syncing</span>
                                </>
                              ) : (
                                <>
                                  <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                                  <span className="text-[11px] text-green-600">Connected</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <button className="rounded-lg p-1.5 text-gray-400 opacity-0 transition-all duration-150 hover:bg-gray-100 hover:text-gray-600 group-hover:opacity-100">
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between border-t border-black/[0.04] pt-3">
                        <span className="text-[11px] text-gray-400">Last sync: {int.lastSync}</span>
                        <span className="text-[11px] font-medium text-gray-500">{int.events24h.toLocaleString()} events / 24h</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Webhook + API Key cards */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Webhook URL */}
                  <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-5">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">Webhook URL</h3>
                        <p className="mt-0.5 text-[11px] text-gray-400">Receive real-time events from Launch OS</p>
                      </div>
                      <button className="flex items-center gap-1.5 rounded-lg border border-black/[0.06] px-2.5 py-1.5 text-xs text-gray-600 transition-all duration-150 hover:bg-gray-50">
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                        </svg>
                        Copy
                      </button>
                    </div>
                    <div className="rounded-lg border border-black/[0.04] bg-gray-50 px-3 py-2.5">
                      <code className="text-xs text-gray-600">https://api.launchos.com/webhooks/org_a8k2m1/ingest</code>
                    </div>
                  </div>

                  {/* API Key */}
                  <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-5">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">API Key</h3>
                        <p className="mt-0.5 text-[11px] text-gray-400">Authenticate external requests</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button className="flex items-center gap-1.5 rounded-lg border border-black/[0.06] px-2.5 py-1.5 text-xs text-gray-600 transition-all duration-150 hover:bg-gray-50">
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          Show
                        </button>
                        <button className="flex items-center gap-1.5 rounded-lg border border-black/[0.06] px-2.5 py-1.5 text-xs text-gray-600 transition-all duration-150 hover:bg-gray-50">
                          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                          </svg>
                          Copy
                        </button>
                      </div>
                    </div>
                    <div className="rounded-lg border border-black/[0.04] bg-gray-50 px-3 py-2.5">
                      <code className="text-xs text-gray-600">sk_live_••••••••••••••••••••••••••••</code>
                    </div>
                  </div>
                </div>

                {/* Connect New Integration */}
                <div>
                  <button className="flex items-center gap-2 rounded-lg border border-dashed border-black/[0.12] px-4 py-2.5 text-[13px] text-gray-500 transition-all duration-150 hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700">
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Connect New Integration
                  </button>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                TAB — NOTIFICATIONS
                ════════════════════════════════════════════════════════════════ */}
            {tab === "notifications" && (
              <div className="space-y-6">
                {/* Header */}
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Notification Rules</h2>
                  <p className="mt-0.5 text-xs text-gray-500">Configure how and when you receive notifications for each event type</p>
                </div>

                {/* Notification rules table */}
                <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] overflow-hidden">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_repeat(4,56px)] items-center gap-2 border-b border-black/[0.04] px-5 py-3">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Event</span>
                    {Object.values(CHANNEL_LABELS).map((label) => (
                      <span key={label} className="text-center text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {label}
                      </span>
                    ))}
                  </div>

                  {/* Rows */}
                  {notifRules.map((rule, ruleIdx) => (
                    <div
                      key={rule.trigger}
                      className={`grid grid-cols-[1fr_repeat(4,56px)] items-center gap-2 px-5 py-3.5 transition-all duration-150 hover:bg-gray-50/60 ${
                        ruleIdx !== notifRules.length - 1 ? "border-b border-black/[0.03]" : ""
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-50 ring-1 ring-black/[0.04]">
                          <NotificationTypeIcon trigger={rule.trigger} />
                        </div>
                        <span className="text-[13px] font-medium text-gray-900">{rule.trigger}</span>
                      </div>
                      {Object.keys(CHANNEL_LABELS).map((ch) => {
                        const isOn = (rule.channels as Record<string, boolean>)[ch];
                        return (
                          <div key={ch} className="flex justify-center">
                            <button
                              onClick={() => toggleNotifChannel(ruleIdx, ch)}
                              className={`flex h-7 w-7 items-center justify-center rounded-lg transition-all duration-150 ${
                                isOn
                                  ? "bg-gray-900 shadow-sm"
                                  : "bg-gray-100 hover:bg-gray-200"
                              }`}
                            >
                              <svg className={`h-3.5 w-3.5 transition-all duration-150 ${isOn ? "text-white" : "text-gray-300"}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                                {ch === "inApp" && <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />}
                                {ch === "email" && <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />}
                                {ch === "slack" && <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />}
                                {ch === "push" && <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />}
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

              </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                TAB — BILLING
                ════════════════════════════════════════════════════════════════ */}
            {tab === "clients" && (
              <div className="mx-auto max-w-4xl space-y-5">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">Clients</h2>
                    <p className="mt-0.5 text-[13px] text-gray-500">The clients your agency manages</p>
                  </div>
                  <button
                    onClick={() => setShowAddClient(true)}
                    className="flex items-center gap-1.5 rounded-lg bg-gray-900 px-3.5 py-2 text-[13px] font-medium text-white shadow-sm transition-all duration-150 hover:bg-gray-800 active:scale-[0.97]"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Add Client
                  </button>
                </div>

                {/* Client cards */}
                <div className="space-y-2.5">
                  {visibleClients.map(({ c: client, idx: clientIdx }) => {
                    const counts = [
                      client.campaigns ? `${client.campaigns} campaign${client.campaigns === 1 ? "" : "s"}` : null,
                      client.deliverables ? `${client.deliverables} doc${client.deliverables === 1 ? "" : "s"}` : null,
                    ]
                      .filter(Boolean)
                      .join(" · ");
                    const link = linkForClient(clientLinks, client);
                    return (
                      <div
                        key={client.initials}
                        className="group flex items-center justify-between rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/[0.04] transition-all duration-150 hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-9 w-9 items-center justify-center rounded-full ${client.primaryColor ? "" : client.color} text-[11px] font-bold text-white`}
                            style={client.primaryColor ? { backgroundColor: client.primaryColor } : undefined}
                          >
                            {client.initials}
                          </div>
                          <div>
                            <h3 className="text-[13px] font-semibold text-gray-900">{client.name}</h3>
                            <p className="text-xs text-gray-400">{client.industry || counts || "No activity yet"}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
                          {link ? (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-blue-200/60"
                              title="Your team can work in this client's Launch OS account"
                            >
                              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                              </svg>
                              {linkedLabel(link)}
                            </span>
                          ) : (
                            <button
                              onClick={() => openLink(client)}
                              className="rounded-lg border border-black/[0.08] bg-white px-3 py-1.5 text-[12px] font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50 active:scale-[0.97]"
                            >
                              Link Launch OS account
                            </button>
                          )}
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-600 ring-1 ring-emerald-200/60">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                          <OverflowMenu
                            label={`Actions for ${client.name}`}
                            items={[
                              ...(link
                                ? [{ label: "Unlink Launch OS account", icon: TRASH_ICON, onClick: () => unlinkClient(link, client.name) }]
                                : []),
                              {
                                label: "Edit",
                                icon: EDIT_ICON,
                                onClick: () => setEditClient({ idx: clientIdx, draft: { ...client, primaryColor: client.primaryColor || "#3B82F6" } }),
                              },
                              {
                                label: "Offboard",
                                icon: TRASH_ICON,
                                destructive: true,
                                onClick: () => archiveClient(client.initials, client.name),
                              },
                            ]}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400">
                  Linking a client's Launch OS account lets your team work in it from anywhere in Launch OS (Sites, Funnels, CRM, and Claude), each person with their own login. Only the agency owner can link or unlink.
                </p>
              </div>
            )}

            {tab === "billing" && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Billing & Subscription</h2>
                  <p className="mt-0.5 text-xs text-gray-500">Manage your plan, payment method, and invoices</p>
                </div>

                {/* Current plan card */}
                <div className="rounded-xl bg-white shadow-sm ring-2 ring-gray-900 p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-gray-900">Pro Plan</h3>
                        <span className="inline-flex items-center rounded-full bg-gray-900 px-2 py-0.5 text-[10px] font-medium text-white">
                          Current Plan
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">Full access to all Launch OS features including AI agents, integrations, and client portals.</p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-gray-900">$299</span>
                      <span className="text-xs text-gray-400">/month</span>
                    </div>
                  </div>

                  {/* Plan features */}
                  <div className="mt-5 grid grid-cols-2 gap-2 border-t border-black/[0.04] pt-5">
                    {[
                      "Unlimited team members",
                      "All integrations",
                      "AI agent orchestration",
                      "Client portals (up to 10)",
                      "Priority support",
                      "Custom branding",
                    ].map((feature) => (
                      <div key={feature} className="flex items-center gap-2">
                        <svg className="h-3.5 w-3.5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                        <span className="text-xs text-gray-600">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Other plans */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Starter */}
                  <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-5 transition-all duration-150 hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-[13px] font-semibold text-gray-900">Starter</h3>
                        <p className="mt-0.5 text-[11px] text-gray-400">For small teams getting started</p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-gray-900">$99</span>
                        <span className="text-[11px] text-gray-400">/mo</span>
                      </div>
                    </div>
                    <button className="mt-4 w-full rounded-lg border border-black/[0.08] py-1.5 text-xs font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50">
                      Downgrade
                    </button>
                  </div>

                  {/* Enterprise */}
                  <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-5 transition-all duration-150 hover:shadow-md">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-[13px] font-semibold text-gray-900">Enterprise</h3>
                        <p className="mt-0.5 text-[11px] text-gray-400">Custom solutions for larger orgs</p>
                      </div>
                      <div className="text-right">
                        <span className="text-lg font-bold text-gray-900">Custom</span>
                      </div>
                    </div>
                    <button className="mt-4 w-full rounded-lg border border-black/[0.08] py-1.5 text-xs font-medium text-gray-700 transition-all duration-150 hover:bg-gray-50">
                      Contact Sales
                    </button>
                  </div>
                </div>

                {/* Payment method */}
                <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-gray-900">Payment Method</h3>
                    <button className="text-xs font-medium text-blue-600 transition-all duration-150 hover:text-blue-700">
                      Update
                    </button>
                  </div>
                  <div className="flex items-center gap-3 rounded-lg border border-black/[0.04] bg-gray-50 p-3">
                    <div className="flex h-8 w-12 items-center justify-center rounded bg-white shadow-sm ring-1 ring-black/[0.06]">
                      <span className="text-[10px] font-bold text-blue-600">VISA</span>
                    </div>
                    <div>
                      <p className="text-[13px] font-medium text-gray-900">Visa ending in 4242</p>
                      <p className="text-[11px] text-gray-400">Expires 12/2027</p>
                    </div>
                  </div>
                </div>

                {/* Recent invoices */}
                <div className="rounded-xl bg-white shadow-sm ring-1 ring-black/[0.04] p-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Recent Invoices</h3>
                  <div className="space-y-0">
                    {[
                      { date: "May 1, 2026", amount: "$299.00", status: "Paid" },
                      { date: "Apr 1, 2026", amount: "$299.00", status: "Paid" },
                      { date: "Mar 1, 2026", amount: "$299.00", status: "Paid" },
                    ].map((invoice, idx) => (
                      <div
                        key={invoice.date}
                        className={`flex items-center justify-between py-3 ${
                          idx !== 2 ? "border-b border-black/[0.03]" : ""
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                          </svg>
                          <div>
                            <p className="text-[13px] font-medium text-gray-900">{invoice.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[13px] font-medium text-gray-700">{invoice.amount}</span>
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-[10px] font-medium text-green-700">
                            {invoice.status}
                          </span>
                          <button className="text-xs font-medium text-blue-600 transition-all duration-150 hover:text-blue-700">
                            Download
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ── Sticky save footer (not for the Domains / Integrations / Connect Claude tabs) ── */}
          {!(NEW_TAB_KEYS as string[]).includes(tab) && (
          <div className="sticky bottom-0 z-10 border-t border-black/[0.06] bg-white/90 backdrop-blur transition-all duration-150">
            <div className="mx-auto flex max-w-3xl items-center justify-between px-8 py-3">
              <div className="flex items-center gap-2 text-[13px]">
                <span
                  className={`h-2 w-2 rounded-full transition-all duration-150 ${
                    dirty ? "bg-amber-500" : "bg-gray-300"
                  }`}
                />
                <span className={dirty ? "font-medium text-gray-700" : "text-gray-400"}>
                  {dirty ? "Unsaved changes" : "All changes saved"}
                </span>
              </div>
              <button
                onClick={handleSave}
                disabled={!dirty || savingProfile}
                className={`rounded-lg px-4 py-2 text-[13px] font-medium shadow-sm transition-all duration-150 ${
                  dirty && !savingProfile
                    ? "bg-gray-900 text-white hover:bg-gray-800 active:scale-[0.97]"
                    : "cursor-not-allowed bg-gray-100 text-gray-400"
                }`}
              >
                {savingProfile ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* ════════════════════ Edit Client modal ════════════════════ */}
      {editClient && (
        <Modal title={`Edit ${editClient.draft.name}`} onClose={() => setEditClient(null)} onSave={saveEditClient}>
          <Field
            label="Client Name"
            value={editClient.draft.name}
            onChange={(v) => setEditClient((s) => (s ? { ...s, draft: { ...s.draft, name: v } } : s))}
          />
          <Field
            label="Industry"
            value={editClient.draft.industry}
            onChange={(v) => setEditClient((s) => (s ? { ...s, draft: { ...s.draft, industry: v } } : s))}
          />
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Primary Brand Color</label>
            <div className="flex items-center gap-3">
              <div
                className="h-9 w-9 shrink-0 rounded-lg shadow-sm ring-1 ring-black/[0.06]"
                style={{ backgroundColor: editClient.draft.primaryColor }}
              />
              <input
                type="text"
                value={editClient.draft.primaryColor}
                onChange={(e) => setEditClient((s) => (s ? { ...s, draft: { ...s.draft, primaryColor: e.target.value } } : s))}
                className="flex-1 rounded-lg border border-black/[0.08] bg-white px-3 py-2 font-mono text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="#000000"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ════════════════════ Edit Team Member modal ════════════════════ */}
      {editMember && (
        <Modal title={`Edit ${editMember.draft.name}`} onClose={() => setEditMember(null)} onSave={saveEditMember}>
          <Field
            label="Name"
            value={editMember.draft.name}
            onChange={(v) => setEditMember((s) => (s ? { ...s, draft: { ...s.draft, name: v } } : s))}
          />
          <Field
            label="Email"
            type="email"
            value={editMember.draft.email}
            onChange={(v) => setEditMember((s) => (s ? { ...s, draft: { ...s.draft, email: v } } : s))}
          />
        </Modal>
      )}

      {/* ════════════════════ Add Client modal ════════════════════ */}
      {linkTarget && (
        <Modal title={`Link ${linkTarget.name}'s Launch OS account`} onClose={closeLink} onSave={submitLink} saveLabel={linkBusy ? "Linking…" : "Link account"}>
          <p className="text-[13px] text-gray-600">
            Sign in to the client's Launch OS account once. After that, your team can work in it from Launch OS and from Claude, without the password. The password isn't saved.
          </p>
          <Field label="Client account email" value={linkEmail} onChange={setLinkEmail} placeholder="marketing@client.com" />
          <Field label="Password" type="password" value={linkPassword} onChange={setLinkPassword} />
          {linkError && <p className="text-[12px] text-red-600">{linkError}</p>}
        </Modal>
      )}

      {showAddClient && (
        <Modal title="Add Client" saveLabel="Add Client" onClose={() => setShowAddClient(false)} onSave={createClient}>
          <Field
            label="Client Name"
            value={newClient.name}
            placeholder="e.g. Cascade Credit Union"
            onChange={(v) => setNewClient((s) => ({ ...s, name: v }))}
          />
          <Field
            label="Industry"
            value={newClient.industry}
            placeholder="Community Banking"
            onChange={(v) => setNewClient((s) => ({ ...s, industry: v }))}
          />
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">Primary Brand Color</label>
            <div className="flex items-center gap-3">
              <div
                className="h-9 w-9 shrink-0 rounded-lg shadow-sm ring-1 ring-black/[0.06]"
                style={{ backgroundColor: newClient.primaryColor }}
              />
              <input
                type="text"
                value={newClient.primaryColor}
                onChange={(e) => setNewClient((s) => ({ ...s, primaryColor: e.target.value }))}
                className="flex-1 rounded-lg border border-black/[0.08] bg-white px-3 py-2 font-mono text-[13px] text-gray-700 transition-all duration-150 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                placeholder="#000000"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* ════════════════════ Invite Team Member modal ════════════════════ */}
      {showInviteMember && (
        <Modal title="Invite Team Member" saveLabel="Send Invite" onClose={() => setShowInviteMember(false)} onSave={createMember}>
          <Field
            label="Name"
            value={newMember.name}
            placeholder="e.g. Jordan Reyes"
            onChange={(v) => setNewMember((s) => ({ ...s, name: v }))}
          />
          <Field
            label="Email"
            type="email"
            value={newMember.email}
            placeholder="name@ambidtech.com"
            onChange={(v) => setNewMember((s) => ({ ...s, email: v }))}
          />
        </Modal>
      )}

      {/* ════════════════════ Undo toast ════════════════════ */}
      {toast && <UndoToast message={toast.message} onUndo={() => { toast.onUndo(); dismissToast(); }} onDismiss={dismissToast} />}
    </AppShellV8>
  );
}
